/* TODO: t() */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { Component, Fragment } from 'react';
import Dropzone from 'react-dropzone';
import { TouchableOpacity, View, StyleSheet, Image } from 'react-native';

import { themeColors } from './Constants';
import Icon from './Icon';

import Thumbnail from '../Preview/Thumbnail';
import Text from './Text';
import TextInput from './TextInput';
import ModalFull from './ModalFull';
import ProgressBar from './ProgressBar';

import { DocumentTypes, ImageTypes } from '../../core/constants/MimeTypes';
import { createHexId } from '../../support/createHexId';
import createViewComponent from '../../support/rn-web/createViewComponent';

import { AttachmentPreview } from '../../support/AttachmentPreview';
import {
  getDownloadURL,
  getFileURL,
  toFile,
} from '../../support/helpers/Files';

const LinkView = createViewComponent({
  tagName: 'a',
  additionalProps: {
    download: true,
  },
});

type Props = {
  imageOnly?: boolean;
  acceptMultipleFiles?: boolean;
  files: Array<AttachmentPreview>;
  onFileDropped: (arg0: Array<AttachmentPreview>) => void;
  onFileChange: (arg0: Array<AttachmentPreview>) => void;
  onFileDelete: (
    arg0: Array<AttachmentPreview>,
    arg1: AttachmentPreview,
  ) => void;
};

type State = {
  renameText: string;
  renameIdx: number;
  isModal: boolean;
  activeFile: AttachmentPreview | null | undefined;
};

// TODO: Factor this into separate components:
// 1. A parent component to render all files and render the "add" thumbnail.
// 2. Render a single file (this would have the preview/buttons and edit state).
// 3. Render the "add" thumbnail with dropzone.
class FileUpload extends Component<Props, State> {
  static defaultProps = {
    acceptMultipleFiles: false,
    imageOnly: false,
  };

  state: State = {
    renameIdx: -1,
    renameText: '',
    activeFile: null,
    isModal: false,
  };

  render() {
    let { files, acceptMultipleFiles, imageOnly } = this.props;
    let allowedMIMEType = Object.keys(imageOnly ? ImageTypes : DocumentTypes);
    return (
      <View style={styles.root}>
        {files.map((file, idx) => {
          return (
            // TODO: Clicking this (or dropping a file here) should allow the
            // user to replace the image.
            <View key={idx} style={styles.previewWrapper}>
              {this._renderPreview(file)}
              {this._renderControls(file, idx)}
            </View>
          );
        })}
        <View style={styles.dropzoneContainer}>
          <Dropzone
            onDropAccepted={this._onDropAccepted}
            multiple={acceptMultipleFiles}
            style={dropzoneStyle}
            {...({ accept: allowedMIMEType } as any)}
          >
            <View style={styles.dropzoneContent}>
              <Icon name="addAttachment" size="M" style={styles.iconStyle} />
              <Text type="label">Insert</Text>
              <Text type="label">Attachment</Text>
            </View>
          </Dropzone>
        </View>
        {this._renderModalPreview()}
      </View>
    );
  }

  // TODO: This logic seems to be duplicated from Content; Abstract this.
  _renderModalPreview = () => {
    let { activeFile, isModal } = this.state;
    if (!activeFile || !isModal) {
      return null;
    }
    let file = activeFile;
    let modalPreview = (content: ReactElement) => {
      return (
        <ModalFull
          isVisible
          onClose={this._showModalPDF}
          title={file.fileName || 'File Preview'}
        >
          {content}
        </ModalFull>
      );
    };
    if (ImageTypes[file.fileType]) {
      return modalPreview(
        <View style={styles.imagePreview}>
          <Image
            source={{ uri: getFileURL(toFile(file), { size: 'large' }) }}
            style={{ width: 800, height: 600 }}
            resizeMode="contain"
            resizeMethod="scale"
          />
        </View>,
      );
    } else if (DocumentTypes[file.fileType] === 'pdf') {
      // TODO: Use rasterization (preview api) to handle files other than PDF.
      return modalPreview(
        <embed
          type={file.fileType}
          src={getFileURL(toFile(file))}
          style={{ flex: 1, maxHeight: 800 }}
        />,
      );
    } else {
      let href = getDownloadURL(toFile(file));
      return modalPreview(
        <LinkView href={href} download={true} style={styles.iconDownload}>
          <Icon name="download" size="XXL" />
          <Text style={{ marginTop: 100 }}>
            {`Sorry this file can't be previewed, click icon for download. `}
          </Text>
        </LinkView>,
      );
    }
  };

  _renderPreview = (file: AttachmentPreview) => {
    if (file.uploadProgress === 100) {
      // Render preview when upload is complete
      return (
        <Thumbnail
          width={180}
          height={135}
          file={{
            id: file.id,
            name: file.fileName,
            size: file.size,
            type: file.fileType,
            preview: file.preview,
          }}
          onPreview={() => {
            this._setModalPDF(file);
          }}
        />
      );
    } else {
      // Render progressbar when upload is in progress
      return (
        <View style={styles.preview}>
          <ProgressBar
            percentage={file.uploadProgress}
            style={{ width: 100 }}
          />
        </View>
      );
    }
  };

  _renderControls = (file: AttachmentPreview, idx: number) => {
    let { renameIdx, renameText } = this.state;
    return (
      <Fragment>
        <View style={styles.buttonContainer}>
          <TouchableOpacity
            accessibilityLabel="Delete"
            style={styles.buttonIcon}
            onPress={() => this._onDeleteFile(idx)}
          >
            <Icon name="remove" size="M" />
          </TouchableOpacity>
          <TouchableOpacity
            accessibilityLabel="Rename"
            style={styles.buttonIcon}
            onPress={() => this._onRenameFile(idx)}
          >
            <Icon name="rename" size="M" color="#000" />
          </TouchableOpacity>
        </View>
        {renameIdx === idx ? ( // We should also show the text input if the user clicks on the
          // text (click to edit).
          <TextInput
            autoFocus
            value={renameText}
            onChangeText={this._onRename}
            onBlur={this._onSaveRename}
            style={styles.renameInput}
          />
        ) : (
          <Text type="label" style={styles.fileName} numberOfLines={1}>
            {file.fileName}
          </Text>
        )}
      </Fragment>
    );
  };

  _onDropAccepted = (files: Array<File>) => {
    // TODO: Simplify this AttachmentPreview type so that it contains just the
    // File object and the fileName (user-edited).
    this.props.onFileDropped(
      files.map((file) => ({
        file,
        // The prefix is to indicate the file has not yet been fully uploaded.
        id: '_' + createHexId(),
        fileType: file.type,
        fileName: file.name,
        preview: URL.createObjectURL(file),
        uploadProgress: 0,
        size: file.size,
      })),
    );
  };

  _onRename = (renameText: string) => {
    this.setState({ renameText });
  };

  _onRenameFile = (renameIdx: number) => {
    this.setState({
      renameIdx,
      renameText: this.props.files[renameIdx]?.fileName ?? '',
    });
  };

  _onSaveRename = () => {
    let { renameIdx, renameText } = this.state;
    let { files, onFileChange } = this.props;
    let newFileList = files.map((file, idx) =>
      renameIdx === idx ? { ...file, fileName: renameText } : file,
    );

    this.setState({ renameIdx: -1, renameText: '' });
    onFileChange(newFileList);
  };

  _onDeleteFile = (fileIdx: number) => {
    let { files, onFileDelete } = this.props;
    let fileList = files.filter((file, idx) => idx !== fileIdx);
    let file = files[fileIdx];
    file && onFileDelete(fileList, file);
  };

  _setModalPDF = (file?: AttachmentPreview) => {
    this.setState((state) => {
      return { isModal: !state.isModal, activeFile: file };
    });
  };

  _showModalPDF = () => {
    this.setState((state) => {
      return { isModal: !state.isModal };
    });
  };
}

// Dropzone requires us to pass plain object for style.
let dropzoneStyle = {
  display: 'flex',
  height: 150,
  width: 150,
};

let styles = StyleSheet.create({
  root: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  dropzoneContainer: {
    margin: 5,
    borderWidth: 1,
    borderColor: themeColors.borderGrey,
    borderRadius: 4,
  },
  dropzoneContent: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    userSelect: 'none',
  },
  previewWrapper: {
    padding: 5,
    alignItems: 'center',
  },
  iconStyle: {
    margin: 8,
  },
  buttonContainer: {
    position: 'absolute',
    top: 10,
    right: 8,
    flexDirection: 'row',
  },
  buttonIcon: {
    backgroundColor: themeColors.white,
    opacity: 0.7,
    borderRadius: 2,
    marginHorizontal: 2,
  },
  preview: {
    width: 150,
    height: 150,
    borderWidth: 1,
    borderColor: themeColors.borderGrey,
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
  },
  fileName: {
    position: 'absolute',
    flex: 1,
    bottom: 5,
    width: 150,
    padding: 2,
    borderWidth: 0,
    backgroundColor: themeColors.grey,
    textAlign: 'center',
  },
  renameInput: {
    position: 'absolute',
    bottom: 5,
    width: 150,
    borderWidth: 0,
    backgroundColor: themeColors.white,
  },
  imagePreview: {
    backgroundColor: 'transparent',
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
  },
  iconDownload: {
    backgroundColor: 'transparent',
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
  },
});

export default FileUpload;
